import { colorCode } from '@/services/CommonVars.js'

import KML from 'ol/format/KML'
import * as olProj from 'ol/proj'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'

// ---- export functions ----

function addKmlLayer(name, fileName, projection) {
  return new VectorLayer({
    source: new VectorSource({
      url: fileName, // ACTION: make apiUrl dependent on environment,
      format: new KML({
        projection: projection
      })
    }),
    name: name
  })
}

// function to calculate the distance
function calcDistance(
  latitudeFrom,
  longitudeFrom,
  latitudeTo,
  longitudeTo
  /**
   * haversineGreatCircleDistance formula
   * Calculates the great-circle distance between two points, with
   * the Haversine formula. Formula from internet
   * @param float $latitudeFrom Latitude of start point in [deg decimal]
   * @param float $longitudeFrom Longitude of start point in [deg decimal]
   * @param float $latitudeTo Latitude of target point in [deg decimal]
   * @param float $longitudeTo Longitude of target point in [deg decimal]
   * @param float $earthRadius Mean earth radius in [m]
   * @return float Distance between points in [m] (same as earthRadius)
   */
) {
  let earthRadius = 6371000
  let latFrom = deg2rad(latitudeFrom)
  let lonFrom = deg2rad(longitudeFrom)
  let latTo = deg2rad(latitudeTo)
  let lonTo = deg2rad(longitudeTo)

  let latDelta = latTo - latFrom
  let lonDelta = lonTo - lonFrom

  // Math.asin() = asin
  // Math.sqrt() =

  let angle =
    2 *
    Math.asin(
      Math.sqrt(
        Math.pow(Math.sin(latDelta / 2), 2) +
          Math.cos(latFrom) *
            Math.cos(latTo) *
            Math.pow(Math.sin(lonDelta / 2), 2)
      )
    )

  return angle * earthRadius
}

// tests if extend one is inside extend two
function extOneInExtTwo(
  extentOneMin,
  extentOneMax,
  extentTwoMin,
  extentTwoMax
) {
  // this function evaluates if extent one is completely inside extent two
  if (
    extentOneMin[0] > extentTwoMin[0] &&
    extentOneMin[1] > extentTwoMin[1] &&
    extentOneMax[0] < extentTwoMax[0] &&
    extentOneMax[1] < extentTwoMax[1]
  ) {
    return true
  } else return false
}

function extractAxisFromStatsData(statsData, params) {
  //
  var xAxis = []
  var yAxis = []
  var typeClass = ''

  // filter only the required records
  var tempStats = statsData.filter(record => {
    var recDateFrom = new Date(record.beginDatetime).toISOString().substr(0, 10)
    var recDateTo = new Date(record.beginDatetime).toISOString().substr(0, 10)
    return (
      recDateFrom >= params.dateFrom &&
      recDateTo <= params.dateTo &&
      (params.typesFilter.length == 0 ||
        params.typesFilter.indexOf(record.typeCode) != -1)
    )
  })

  tempStats.forEach(record => {
    var period = getPeriod(record.beginDatetime, params.periodType)

    record.period = period
    if (xAxis.indexOf(period) === -1) {
      xAxis.push(period)
    }
  })

  xAxis.sort()

  statsData.forEach(record => {
    params.typeClass == 'type'
      ? (typeClass = 'typeCode')
      : (typeClass = 'subtypeCode')

    if (yAxis.indexOf(record[typeClass]) === -1) {
      yAxis.push(record[typeClass])
    }
  })

  yAxis.sort()

  return { xAxis: xAxis, yAxis: yAxis }
}

// create datasets required for the chartjs object
function getDataSets(axis, yAxisById, statsData, typeField, measure) {
  var initX = []
  var dataArray = []

  // first initialize the x/y-Axis matrix so that each cell contains a 0
  for (let y = 0; y < yAxisById.length; y++) {
    for (let x = 0; x < axis.xAxis.length; x++) {
      initX.push(0)
    }
    dataArray.push(initX)
    initX = []
  }

  // loop through static data and add new record value to previous value
  statsData.forEach(record => {
    // create datasets data
    let xAxisIdx = axis.xAxis.indexOf(record.period) // get xAxis index of record
    let yAxisIdx = yAxisById.indexOf(record.typeFieldId) // get yAxis index of record
    let previousValue = dataArray[yAxisIdx][xAxisIdx] // get the current value stored at this location
    if (measure === 'count') {
      dataArray[yAxisIdx][xAxisIdx] = previousValue + 1 // add new value to pervious value and write to matrix cell
    } else {
      // this section is not working
      dataArray[yAxisIdx][xAxisIdx] = record[measure] + previousValue // add new value to pervious value and write to matrix cell
    }
  })

  // write all data to the datasetArray
  var datasetArray = []
  for (let y = 0; y < yAxisById.length; y++) {
    // the y-Axis is a record in the dataset
    let colorCodeIdx = typeField.substr(0,4) + yAxisById[y] // derive name for color code
    datasetArray.push({
      label: axis.yAxis[y], // label is the x-Axis
      data: dataArray[y], // data is the dataArray
      backgroundColor: colorCode[colorCodeIdx].backgroundColor, // add the color code derived from the CommonVars.js file
      categoryPercentage: 0.7 // size of the category in the chart
    })
    //}
  }

  return datasetArray
}

// Returns the number of the week for a given date (date object)
function getNumberOfWeek(currDate) {
  const firstDayOfYear = new Date(currDate.getFullYear(), 0, 1);
  const pastDaysOfYear = (currDate - firstDayOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

function getPeriod(date, periodType) {
  var beginDate = new Date(date) // begin date of track
  var year = beginDate.getFullYear() // extracted full year
  var quarter = 'Q' + Math.ceil((beginDate.getMonth() + 1) / 3) // extract quarter
  var month = `${beginDate.getMonth() + 1}`.padStart(2, '0') // extracted month

  // extract week
  var onejan = new Date(beginDate.getFullYear(), 0, 1)
  var dayOfYear = (beginDate - onejan + 86400000) / 86400000
  var week = `${Math.ceil(dayOfYear / 7)}`.padStart(2, '0') // extracted week
  var day = `${beginDate.getDay() + 1}`.padStart(2, '0') // extracted day

  if (periodType === 'Y') {
    return year
  } else if (periodType === 'Q') {
    return year + '-' + quarter
  } else if (periodType === 'M') {
    return year + '-' + month
  } else if (periodType === 'W') {
    return year + '-' + week
  } else return year + '-' + month + '-' + day
}

function getPeriodAxis(dateFrom, dateTo, period, periodType) {
  /* function returning axis data for chart (Y|S|Q|M|W)
      Y = Years
      Q = Quarters
      M = Months
      W = Weeks
      D = Days - not yet implemented

      Params: 
      dateFrom, dateTo, period, periodType
  */

  let fromDate = dateFrom ? new Date(dateFrom) : new Date("1900", "1", "1")
  let toDate = dateTo ? new Date(dateTo) : new Date()
  //let periodType = periodType
  let axis = []

  // return years
  if ( periodType === 'Y' ) {

    let yearFrom = new Date(fromDate).toISOString().substr(0, 4)
    let yearTo = new Date(toDate).toISOString().substr(0, 4)
    let years = yearTo - yearFrom
    for ( let i=0; i<=years; i++  ) {
      axis.push( +yearFrom + +i)
    }
    return axis
  }

  // return quarters (yyyy-Qn) 
  if ( periodType === 'Q' ) {
    let yearFrom = new Date(fromDate).toISOString().substr(0, 4)
    let yearTo = new Date(toDate).toISOString().substr(0, 4)

    let years = yearTo - yearFrom
    let lastQuarter = Math.floor(((Number(new Date(toDate).toISOString().substr(5,2))-1) + 3) / 3);
    lastQuarter > 4 ? lastQuarter - 4 : lastQuarter

    for ( let year=0; year<=years; year++  ) {
      for ( let quarter=1; quarter<=4; quarter++ ) {
        if ( year === years && quarter > lastQuarter  ) {
          break
        } else {
          axis.push( String(+yearFrom + +year) + "-Q" + quarter )
        }
      }
    }
    return axis
  }

  // return month (YYYY-MM) 
  if ( periodType === 'M' ) {
    let yearFrom = new Date(fromDate).toISOString().substr(0, 4)
    let yearTo = new Date(toDate).toISOString().substr(0, 4)

    let years = yearTo - yearFrom
    let lastMonth = Number(toDate.substr(5,2))

    for ( let year=0; year<=years; year++  ) {
      for ( let month=1; month<=12; month++ ) {
        if ( year === years && month > lastMonth  ) {
          break
        } else {
          axis.push( String(+yearFrom + +year) + "-" + String(month).padStart(2, '0') )
        }
      }
    }
    return axis
  }

  // return week (YYYY-Wnn) 
  if ( periodType === 'W' ) {
    let yearFrom = new Date(fromDate).toISOString().substr(0, 4)
    let yearTo = new Date(toDate).toISOString().substr(0, 4)

    let years = yearTo - yearFrom
    let lastWeek = getNumberOfWeek(new Date(toDate)) - +1

    for ( let year=0; year<=years; year++  ) {
      for ( let week=1; week<=53; week++ ) {
        if ( year === years && week > lastWeek  ) {
          break
        } else {
          axis.push( String(+yearFrom + +year) + "-W" + String(week).padStart(2, '0') )
        }
      }
    }
    return axis
  }
}

function touchedAreas(track, area, subtypeCode) {
  /* function returning ids of areas touched by the given track
     Params: track - track object containing the corner coodinates
             area - containing also the corner coordinates
     Returns 0 if not touching or 1 if touching
  */
  
  let touched = 0 // counting number of touches

  // Function to evaluate if we have a touch
  let isTouching = function(track, area) {
    // function checks if track is inside area
    // param canton is only required for debugging
    
    if ( 
      // true if area is outside all 4 dimensions
      area.lonEast < track.coord.coordBottomLeftLon || // area is completely west of track
      area.latNorth < track.coord.coordBottomRightLat || // area is completely south of track 
      area.latSouth > track.coord.coordTopLeftLat || // area is completely north of track
      area.lonWest > track.coord.coordTopRightLon ) { // area is completely east of track
        // console.log("NotTouching: track completely outside area")
        return 0
    } else {
      // console.log("isTouching: track potentially inside area")  
      return 1
    }
  }

  touched = isTouching(track, area)

  if ( touched ) {
    console.log("Potentially touching %s: %s", subtypeCode, area.code)
    return {
      areaCode: area.code,
      areaSubcode: area.subcode,
      areaName: area.name,
      id: area.id, 
    } 
  }
}

function returnUniqueAreas(areas, subtypeCode) {
  /* This function takes the areas array and returns a minimal array of unique areas
     This is required because some areas do have different sub areas (e.g. Japan with its island) */

  let areasUnique = [] // array containing the unique areas
  let exists = false // indicates of area already exists
  let subareas = [] // subareas belonging to same area
  
  // we loop through every record
  areas.forEach(area => {
    if ( area.subtypeCode === subtypeCode ) {
      exists = areasUnique.some(rec => rec.areaCode === area.code )
      if ( !exists ) {
        {
          // we do not have this area in the cantontUnique array; hence we push it in
          areasUnique.push({id: area.id, areaCode: area.code, areaName: area.name })
        }       
      }
    }
  })
  return areasUnique
}

function transformExtent(extentMin, extentMax, projFrom, projTo) {
  // this function takes two points in the projection from format
  // and returns an extent in the projection to format
  let extMin = olProj.transform(extentMin, projFrom, projTo)
  let extMax = olProj.transform(extentMax, projFrom, projTo)
  return extMin.concat(extMax)
}

// Convert WGS lat/long (° dec) to CH y -- currently not used
function WGStoCHy(latitude, longitude) {
  // Converts decimal degrees sexagesimal seconds
  const lat = DECtoSEX(latitude)
  const lon = DECtoSEX(longitude)

  // Auxiliary values (% Bern)
  const lat_aux = (lat - 169028.66) / 10000
  const lon_aux = (lon - 26782.5) / 10000

  // Process Y
  const y =
    600072.37 +
    211455.93 * lon_aux -
    10938.51 * lon_aux * lat_aux -
    0.36 * lon_aux * Math.pow(lat_aux, 2) -
    44.54 * Math.pow(lon_aux, 3)

  return y
}

// Convert WGS lat/long (° dec) to CH x -- currently not used
function WGStoCHx(latitude, longitude) {
  // Converts decimal degrees sexagesimal seconds
  const lat = DECtoSEX(latitude)
  const lon = DECtoSEX(longitude)
  // Auxiliary values (% Bern)
  const lat_aux = (lat - 169028.66) / 10000
  const lon_aux = (lon - 26782.5) / 10000

  // Process X
  const x =
    200147.07 +
    308807.95 * lat_aux +
    3745.25 * Math.pow(lon_aux, 2) +
    76.63 * Math.pow(lat_aux, 2) -
    194.56 * Math.pow(lon_aux, 2) * lat_aux +
    119.79 * Math.pow(lat_aux, 3)

  return x
}

export {
  addKmlLayer,
  calcDistance,
  extOneInExtTwo,
  extractAxisFromStatsData,
  getDataSets,
  getPeriod,
  getPeriodAxis,
  returnUniqueAreas,
  touchedAreas,
  transformExtent,
  WGStoCHy,
  WGStoCHx,
}

// ---- internal functions ----

// Convert DEC angle to SEX DMS -- currently not used
function DECtoSEX(angle) {
  // Extract DMS
  const deg = parseInt(angle)
  const min = parseInt((angle - deg) * 60)
  const sec = ((angle - deg) * 60 - min) * 60
  // Result in sexagesimal seconds
  return sec + min * 60 + deg * 3600.0
}

// currently not used
function deg2rad(degrees) {
  var pi = Math.PI
  return degrees * (pi / 180)
}
